/**
 * Sidebar menu icon registry ? Outlined only (MUI *Outlined + custom domain icons).
 * Keys match `admin_menu.menu_icon` (legacy filled names + *Outlined aliases).
 */
import type {ElementType} from 'react';

import ManageAccountsOutlinedIcon from '@mui/icons-material/ManageAccountsOutlined';
import MenuOutlinedIcon from '@mui/icons-material/MenuOutlined';
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined';
import ManageHistoryOutlinedIcon from '@mui/icons-material/ManageHistoryOutlined';
import DocumentScannerOutlinedIcon from '@mui/icons-material/DocumentScannerOutlined';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import PlaylistAddCheckOutlinedIcon from '@mui/icons-material/PlaylistAddCheckOutlined';
import DonutSmallOutlinedIcon from '@mui/icons-material/DonutSmallOutlined';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import RuleOutlinedIcon from '@mui/icons-material/RuleOutlined';
import WidgetsOutlinedIcon from '@mui/icons-material/WidgetsOutlined';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import PolicyOutlinedIcon from '@mui/icons-material/PolicyOutlined';
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import BusinessCenterOutlinedIcon from '@mui/icons-material/BusinessCenterOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import SchemaOutlinedIcon from '@mui/icons-material/SchemaOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import FeedbackOutlinedIcon from '@mui/icons-material/FeedbackOutlined';
import SettingsApplicationsOutlinedIcon from '@mui/icons-material/SettingsApplicationsOutlined';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import CurrencyExchangeOutlinedIcon from '@mui/icons-material/CurrencyExchangeOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import CalculateOutlinedIcon from '@mui/icons-material/CalculateOutlined';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';

import {
    AdminRootOutlinedIcon,
    CompanyInfoOutlinedIcon,
    AccountRootOutlinedIcon,
    AuditRootOutlinedIcon,
    FinanceStatementsOutlinedIcon,
    ScheduleOutlinedIcon,
    TaxOutlinedIcon,
    LoanOutlinedIcon,
    AfsOutlinedIcon,
    GeneralLedgerOutlinedIcon,
    SysManageOutlinedIcon,
    CateManageOutlinedIcon,
    DicManageOutlinedIcon,
    BankDicOutlinedIcon,
    EquityTxOutlinedIcon,
    TaxLimitOutlinedIcon,
} from '@/assets/icons/MenuOutlinedIcons';

function alias(keys: string[], icon: ElementType): Record<string, ElementType> {
    const out: Record<string, ElementType> = {};
    for (const k of keys) out[k] = icon;
    return out;
}

export const menuIconMap: Record<string, ElementType> = {
    // Top-level modules (custom)
    ...alias(['AdminPanelSettingsIcon', 'AdminPanelSettingsOutlinedIcon'], AdminRootOutlinedIcon),
    ...alias(['CorporateFareIcon', 'CorporateFareOutlinedIcon'], CompanyInfoOutlinedIcon),
    ...alias(['AccountBalanceIcon', 'AccountBalanceOutlinedIcon'], AccountRootOutlinedIcon),
    ...alias(['FactCheckIcon', 'FactCheckOutlinedIcon'], AuditRootOutlinedIcon),

    // Admin / sys
    ...alias(['SettingsIcon', 'SettingsOutlinedIcon'], SysManageOutlinedIcon),
    ...alias(['ManageAccountsIcon', 'ManageAccountsOutlinedIcon'], ManageAccountsOutlinedIcon),
    ...alias(['MenuIcon', 'MenuOutlinedIcon'], MenuOutlinedIcon),
    ...alias(['MonitorHeartIcon', 'MonitorHeartOutlinedIcon'], MonitorHeartOutlinedIcon),
    ...alias(
        ['ManageHistoryIcon', 'ManageHistoryOutlinedIcon', 'HistoryIcon', 'HistoryOutlinedIcon'],
        ManageHistoryOutlinedIcon,
    ),
    ...alias(['DocumentScannerIcon', 'DocumentScannerOutlinedIcon'], DocumentScannerOutlinedIcon),
    ...alias(['SecurityIcon', 'SecurityOutlinedIcon'], SecurityOutlinedIcon),
    ...alias(['FolderSpecialIcon', 'FolderSpecialOutlinedIcon'], CateManageOutlinedIcon),
    ...alias(['TableChartIcon', 'TableChartOutlinedIcon'], TableChartOutlinedIcon),
    ...alias(['PlaylistAddCheckIcon', 'PlaylistAddCheckOutlinedIcon'], PlaylistAddCheckOutlinedIcon),
    ...alias(['DonutSmallIcon', 'DonutSmallOutlinedIcon'], DonutSmallOutlinedIcon),
    ...alias(['BarChartIcon', 'BarChartOutlinedIcon'], BarChartOutlinedIcon),
    ...alias(['LibraryBooksIcon', 'LibraryBooksOutlinedIcon'], DicManageOutlinedIcon),
    ...alias(['MenuBookIcon', 'MenuBookOutlinedIcon'], MenuBookOutlinedIcon),
    ...alias(['RuleIcon', 'RuleOutlinedIcon'], RuleOutlinedIcon),
    ...alias(['WidgetsIcon', 'WidgetsOutlinedIcon'], WidgetsOutlinedIcon),
    ...alias(['VerifiedIcon', 'VerifiedOutlinedIcon'], VerifiedOutlinedIcon),
    ...alias(['PolicyIcon', 'PolicyOutlinedIcon'], PolicyOutlinedIcon),
    ...alias(['FolderOpenIcon', 'FolderOpenOutlinedIcon'], FolderOpenOutlinedIcon),
    ...alias(['AssessmentIcon', 'AssessmentOutlinedIcon'], AssessmentOutlinedIcon),

    // Company
    ...alias(['BusinessIcon', 'BusinessOutlinedIcon'], BusinessOutlinedIcon),
    ...alias(['CalendarMonthIcon', 'CalendarMonthOutlinedIcon'], CalendarMonthOutlinedIcon),
    ...alias(['BusinessCenterIcon', 'BusinessCenterOutlinedIcon'], BusinessCenterOutlinedIcon),
    ...alias(['SwapHorizIcon', 'SwapHorizOutlinedIcon'], EquityTxOutlinedIcon),

    // Account / GL
    ...alias(
        ['ReceiptLongIcon', 'ReceiptLongOutlinedIcon', 'GeneralLedgerIcon', 'GeneralLedgerOutlinedIcon'],
        GeneralLedgerOutlinedIcon,
    ),

    // Audit domain
    ...alias(
        [
            'FinanceStatementsIcon',
            'FinanceStatementsOutlinedIcon',
            'DescriptionIcon',
            'DescriptionOutlinedIcon',
        ],
        FinanceStatementsOutlinedIcon,
    ),
    ...alias(
        ['EditNoteIcon', 'EditNoteOutlinedIcon', 'ScheduleIcon', 'ScheduleOutlinedIcon'],
        ScheduleOutlinedIcon,
    ),
    ...alias(['TaxIcon', 'TaxOutlinedIcon'], TaxOutlinedIcon),
    ...alias(
        ['RequestQuoteIcon', 'RequestQuoteOutlinedIcon', 'LoanIcon', 'LoanOutlinedIcon'],
        LoanOutlinedIcon,
    ),
    ...alias(
        ['AccountBalanceWalletIcon', 'AccountBalanceWalletOutlinedIcon', 'AfsIcon', 'AfsOutlinedIcon'],
        AfsOutlinedIcon,
    ),
    ...alias(
        ['TaxLimitIcon', 'TaxLimitOutlinedIcon', 'GavelIcon', 'GavelOutlinedIcon'],
        TaxLimitOutlinedIcon,
    ),
    ...alias(['BankDicIcon', 'BankDicOutlinedIcon', 'AccountBalanceBankIcon'], BankDicOutlinedIcon),

    // Historical extras
    ...alias(['CategoryIcon', 'CategoryOutlinedIcon'], CategoryOutlinedIcon),
    ...alias(['AccountTreeIcon', 'AccountTreeOutlinedIcon'], AccountTreeOutlinedIcon),
    ...alias(['SchemaIcon', 'SchemaOutlinedIcon'], SchemaOutlinedIcon),
    ...alias(['TuneIcon', 'TuneOutlinedIcon'], TuneOutlinedIcon),
    ...alias(['FeedbackIcon', 'FeedbackOutlinedIcon'], FeedbackOutlinedIcon),
    ...alias(['SettingsApplicationsIcon', 'SettingsApplicationsOutlinedIcon'], SettingsApplicationsOutlinedIcon),
    // Tools root (admin_menu.menu_icon = BuildOutlined)
    ...alias(['BuildIcon', 'BuildOutlined', 'BuildOutlinedIcon'], BuildOutlinedIcon),
    // Currency & Rate (admin_menu.menu_icon = CurrencyExchangeOutlined)
    ...alias(
        ['CurrencyExchangeIcon', 'CurrencyExchangeOutlined', 'CurrencyExchangeOutlinedIcon'],
        CurrencyExchangeOutlinedIcon,
    ),
    ...alias(['LinkIcon', 'LinkOutlinedIcon'], LinkOutlinedIcon),
    ...alias(['TrendingUpIcon', 'TrendingUpOutlinedIcon'], TrendingUpOutlinedIcon),
    ...alias(['CalculateIcon', 'CalculateOutlinedIcon'], CalculateOutlinedIcon),
    ...alias(['DashboardIcon', 'DashboardOutlinedIcon'], DashboardOutlinedIcon),
};

export const defaultMenuIcon: ElementType = MenuOutlinedIcon;

export function resolveMenuIcon(menuIcon?: string | null): ElementType {
    if (!menuIcon) return defaultMenuIcon;
    return menuIconMap[menuIcon] ?? defaultMenuIcon;
}
